import React, { useCallback, useMemo } from 'react';
import Timeline from './timeline/Timeline';
import Stage from './stage/Stage';
import Inspector from './inspector/Inspector';
import AnglesPanel from './angles/AnglesPanel';
import AddEffect, { spanForEffect } from './inspector/AddEffect';
import { useTimelineStore } from './state/timelineStore';
import { getLayerByKind, mainSequenceEnd, sortedClips } from './state/timelineModel';
import './editor.css';

/**
 * The edit surface.
 *
 * Three zones in the order the eye needs them: the stage showing what the edit
 * currently is, the inspector for whatever is selected, and the timeline
 * underneath running the full width. One store feeds all three, so the
 * playhead, the selection and the graph have a single definition rather than a
 * copy per panel that has to be kept in step.
 */

function Editor({
    graph,
    assets,
    editable = true,
    onGraphChange,
    resolveAssetUrl,
    onAlignAngles,
    children,
}) {
    const store = useTimelineStore({ graph, assets, onGraphChange });
    const {
        selection, selectedItem, playhead, isPlaying, totalDuration,
        assetsByFileName, actions,
    } = store;

    /* Which clip an effect would attach to: the selected clip, or the one under
       the playhead when an effect (or nothing) is selected. */
    const targetClip = useMemo(() => {
        const clips = sortedClips(getLayerByKind(store.graph, 'video', 'layer 1'));
        // Only a clip on the main track: effects and angles are compiled into
        // the main sequence, so one hung on an inset would never render.
        if (selection?.kind === 'clip') return clips.find(clip => clip.id === selectedItem?.id) || null;
        if (selection?.kind === 'effect' && selectedItem?.clipId) {
            return clips.find(clip => clip.id === selectedItem.clipId) || null;
        }
        return clips.find(clip => playhead >= clip.start && playhead < clip.end) || null;
    }, [selection, selectedItem, store.graph, playhead]);

    /* Whether the selection is an inset, and where the main video ends: the
       inspector offers placement for one and warns when it outruns the other. */
    const insetInfo = useMemo(() => {
        const insets = getLayerByKind(store.graph, 'overlay', 'overlay')?.items || [];
        if (selection?.kind !== 'clip' || !insets.some(inset => inset.id === selection.id)) return null;
        return { mainEnd: mainSequenceEnd(store.graph) };
    }, [store.graph, selection]);

    const handleEffectChange = useCallback((id, patch, coalesce) => {
        actions.updateEffect(id, patch, coalesce);
    }, [actions]);

    /* Changing a fade's direction or length moves where it sits: a fade out
       belongs at the tail, a fade in at the head. Without this the span stays
       where the effect was created and the inspector reports a time the fade no
       longer has. An explicit start/end edit is left alone -- that is the user
       placing it by hand, which outranks the derived position. */
    const handleSelectedChange = useCallback((patch, coalesce) => {
        if (!selection?.id) return;

        const touchesFadeShape = selectedItem?.type === 'fade'
            && patch?.params
            && patch.start === undefined
            && patch.end === undefined;

        if (touchesFadeShape && targetClip) {
            const { start, end } = spanForEffect('fade', patch.params, targetClip);
            actions.updateItem(selection.id, { ...patch, start, end, duration: end - start }, coalesce);
            return;
        }
        actions.updateItem(selection.id, patch, coalesce);
    }, [selection, selectedItem, targetClip, actions]);

    return (
        <div className="nle-editor">
            <div className="nle-editor-top">
                <Stage
                    graph={store.graph}
                    playhead={playhead}
                    isPlaying={isPlaying}
                    totalDuration={totalDuration}
                    selection={selection}
                    assetsByFileName={assetsByFileName}
                    resolveAssetUrl={resolveAssetUrl}
                    editable={editable}
                    onPlayheadChange={actions.setPlayhead}
                    onPlayingChange={actions.setPlaying}
                    onSelect={actions.select}
                    onEffectChange={handleEffectChange}
                />

                <div className="nle-editor-side">
                    <Inspector
                        selectedItem={selectedItem}
                        selection={selection}
                        insetInfo={insetInfo}
                        assets={assets}
                        assetsByFileName={assetsByFileName}
                        editable={editable}
                        onChange={handleSelectedChange}
                        onDelete={() => selection?.id && actions.deleteItem(selection.id)}
                    />
                    <AddEffect
                        clip={targetClip}
                        disabled={!editable}
                        onAdd={actions.addEffect}
                    />
                </div>
            </div>

            <Timeline
                store={store}
                assets={assets}
                editable={editable}
                resolveAssetUrl={resolveAssetUrl}
            />

            {/* Directly under the tracks, because cutting between cameras is a
                timeline gesture: split, pick a camera for this piece, split
                again. It takes the same clip the effect menu does -- the
                selection, or whatever the playhead is sitting on -- so parking
                on a segment is enough to recut it. */}
            <AnglesPanel
                clip={targetClip}
                assets={assets}
                assetsByFileName={assetsByFileName}
                editable={editable}
                onSetAngle={actions.setAngle}
                onAddAngle={actions.addAngle}
                onUpdateMulticam={actions.updateMulticam}
                onAlignAngles={onAlignAngles}
            />

            {children}
        </div>
    );
}

export default Editor;
